"use client";

import React from "react";
import { JsonLdScript } from "@/components/JsonLdScript";
import { getLocalizedPath } from "@/utils/pathUtils";

/**
 * Bus trip data'sını Schema.org formatına dönüştürür
 * @param {Object} trip - Bus trip data objesi
 * @param {string} baseUrl - Base URL (default: "https://www.turna.com")
 * @param {string} locale - Locale (default: "tr")
 * @returns {Object} Schema.org BusTrip objesi
 */
const transformTripToSchema = (trip, baseUrl = "https://www.turna.com", locale = "tr") => {
  // Date'i parse et
  const departureDate = new Date(trip.Date);
  
  // Duration'dan arrival time'ı hesapla
  // Duration formatı: "6s" veya "6s 30d" gibi (saat ve dakika)
  let arrivalDate = new Date(departureDate);
  const durationMatch = trip.Duration?.match(/(\d+)s(?:\s+(\d+)d)?/);
  if (durationMatch) {
    const hours = parseInt(durationMatch[1] || 0, 10);
    const minutes = parseInt(durationMatch[2] || 0, 10);
    arrivalDate.setHours(arrivalDate.getHours() + hours);
    arrivalDate.setMinutes(arrivalDate.getMinutes() + minutes);
  } else {
    // Eğer duration parse edilemezse, sadece saat ekle
    arrivalDate.setHours(arrivalDate.getHours() + 6);
  }

  // Date formatını "9.01.2026 19:00:00" formatına çevir
  const formatDateTime = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
  };

  // URL'i oluştur
  // RedirectUrl zaten "/istanbul-anadolu-ankara/2026-01-12" formatında geliyor
  // Bunu "/otobus-bileti/tek-yon/istanbul-anadolu-ankara/2026-01-12" formatına çevirmemiz gerekiyor
  const basePath = getLocalizedPath('/bus-ticket/one-way/', locale);
  const tripUrl = trip.RedirectUrl 
    ? `${baseUrl}${basePath}${trip.RedirectUrl.startsWith('/') ? trip.RedirectUrl.substring(1) : trip.RedirectUrl}`
    : `${baseUrl}${basePath}${trip.FromCitySlug}-${trip.ToCitySlug}`;

  // Route name oluştur
  const routeName = `${trip.FromCity} ${trip.ToCity} Otobüs Bileti`;

  return {
    "@context": "https://schema.org",
    "@type": "BusTrip",
    "name": routeName,
    "url": tripUrl,
    "busName": trip.CompanyName || "",
    "provider": {
      "@type": "Organization",
      "name": trip.CompanyName || "",
      "url": trip.CompanyImageUrl ? `${baseUrl}/otobus-bileti/${trip.CompanyName?.toLowerCase().replace(/\s+/g, '-')}` : undefined
    },
    "departureBusStop": {
      "@type": "BusStation",
      "name": trip.FromStationName || trip.OriginTerminalName || trip.FromCity,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": trip.FromCity,
        "addressCountry": "TR"
      }
    },
    "arrivalBusStop": {
      "@type": "BusStation",
      "name": trip.ToStationName || trip.ToCity,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": trip.ToCity,
        "addressCountry": "TR"
      }
    },
    "departureTime": formatDateTime(departureDate),
    "arrivalTime": formatDateTime(arrivalDate),
    "offers": {
      "@type": "Offer",
      "price": trip.Price?.toFixed(2)?.replace('.', ',') || "0,00",
      "priceCurrency": "TL"
    }
  };
};

/**
 * BusTripSchema Component
 * Bus trip array'ini alır ve Schema.org formatında JSON-LD script tag'leri oluşturur
 * 
 * @param {Object} props
 * @param {Array} props.data - Bus trip array'i
 * @param {number} props.displayLimit - Gösterilecek maksimum trip sayısı (default: tümü)
 * @param {string} props.baseUrl - Base URL (default: "https://www.turna.com")
 * @param {string} props.locale - Locale (default: "tr")
 * @returns {React.Component} JSON-LD script tag'leri
 */
export const BusTripSchema = ({ data, displayLimit, baseUrl = "https://www.turna.com", locale = "tr" }) => {
  // Data kontrolü
  if (!data || !Array.isArray(data) || data.length === 0) {
    return null;
  }

  // displayLimit varsa sınırla
  const limitedTrips = displayLimit && displayLimit > 0 
    ? data.slice(0, displayLimit)
    : data;

  // Her trip için schema oluştur ve script tag'leri döndür
  return (
    <>
      {limitedTrips.map((trip, index) => {
        const schema = transformTripToSchema(trip, baseUrl, locale);
        return (
          <JsonLdScript
            key={`bus-trip-schema-${index}-${trip.Date}-${trip.FromStation}-${trip.ToStation}`}
            id={`bus-trip-schema-${index}`}
            jsonLd={schema}
          />
        );
      })}
    </>
  );
};

export default BusTripSchema;

